import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import './ProductDetailModal.css';
import Toast from '../components/Toast';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import LazyImage from './LazyImage';
import { trackProductView, trackProductShare } from '../utils/analytics';
import SafeHTMLRenderer, { getPlainTextLength } from '../components/SafeHTMLRenderer';

const ProductDetailModal = ({ isOpen, onClose, product, addToCart, catalog = [] }) => {
  const navigate = useNavigate();
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasRelatedProducts, setHasRelatedProducts] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Preload adjacent images for faster navigation
  useEffect(() => {
    if (!isOpen || !product) return;

    const media = getProductMedia();

    // Preload next and previous images
    const preloadImage = (index) => {
      if (media[index] && media[index].type === 'image') {
        const img = new Image();
        img.src = media[index].url;
      }
    };

    // Preload next image
    const nextIndex = (currentMediaIndex + 1) % media.length;
    preloadImage(nextIndex);

    // Preload previous image
    const prevIndex = (currentMediaIndex - 1 + media.length) % media.length;
    preloadImage(prevIndex);

  }, [currentMediaIndex, isOpen, product]);

  useEffect(() => {
    if (isOpen && product) {
      // Reset quantity, media index, and description state when modal opens
      setQuantity(1);
      setCurrentMediaIndex(0);
      setIsDescriptionExpanded(false);

      // Track product view in analytics
      trackProductView(product);

      // Defer related products fetch to avoid blocking modal opening
      if (product.tags && product.tags.length > 0) {
        // Use setTimeout to fetch after modal is displayed
        const timer = setTimeout(() => {
          fetchRelatedProducts();
        }, 300);
        return () => clearTimeout(timer);
      } else {
        setRelatedProducts([]);
        setHasRelatedProducts(false);
      }
    }
  }, [isOpen, product]);

  // Add keyboard navigation support (ESC to close)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Focus management: save current focus and restore on close
    const previouslyFocusedElement = document.activeElement;

    // Focus the close button when modal opens
    const closeButton = document.querySelector('.product-modal-close');
    if (closeButton) {
      closeButton.focus();
    }

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';

      // Restore focus to previously focused element
      if (previouslyFocusedElement && previouslyFocusedElement.focus) {
        previouslyFocusedElement.focus();
      }
    };
  }, [isOpen, onClose]);

  const fetchRelatedProducts = async () => {
    if (!product || !product.tags || product.tags.length === 0) {
      setHasRelatedProducts(false);
      return;
    }

    try {
      setLoading(true);
      
      // First try to find related products from the catalog
      if (catalog && catalog.length > 0) {
        const filtered = catalog
          .filter(item => 
            item.id !== product.id && // Not the current product
            item.isActive !== false && // Only active products
            item.tags && // Has tags
            item.tags.some(tag => product.tags.includes(tag)) // Has at least one matching tag
          )
          .slice(0, 4); // Limit to 4 related products
        
        setRelatedProducts(filtered);
        setHasRelatedProducts(filtered.length > 0);
        setLoading(false);
        return;
      }
      
      // If catalog is not available, fetch from Firestore
      const productsCollection = collection(db, 'products');
      
      // We'll query active products that share at least one tag with the current product
      const relatedQuery = query(
        productsCollection,
        where('isActive', '!=', false)
      );
      
      const querySnapshot = await getDocs(relatedQuery);
      
      const related = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(item => 
          item.id !== product.id && // Not the current product
          item.tags && // Has tags
          item.tags.some(tag => product.tags.includes(tag)) // Has at least one matching tag
        )
        .slice(0, 4); // Limit to 4 related products
      
      setRelatedProducts(related);
      setHasRelatedProducts(related.length > 0);
    } catch (error) {
      console.error('Error fetching related products:', error);
      setHasRelatedProducts(false);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !product) return null;

  // Calculate discounted price if applicable
  const discountedPrice = product.discount > 0
    ? product.price * (1 - product.discount / 100)
    : null;

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }

    // Show toast notification
    setToastMessage(`${quantity} ${product.name} added to cart!`);
    setShowToast(true);

    // Close modal after a brief delay to let user see the toast
    setTimeout(() => {
      onClose();
    }, 500);
  };

  const handleShare = async () => {
    // Create URL with product ID parameter
    const baseUrl = window.location.origin + window.location.pathname;
    const url = `${baseUrl}?product=${product.id}`;
    const title = product.name;
    const text = `Check out ${product.name} at Two Gs Crafting!`;

    try {
      // Try Web Share API first (works on Safari mobile and other mobile browsers)
      if (navigator.share && navigator.canShare && navigator.canShare({ url })) {
        try {
          await navigator.share({
            title: title,
            text: text,
            url: url
          });
          // Track successful share via Web Share API
          trackProductShare(product, 'web_share');
          return;
        } catch (shareError) {
          // User cancelled share, don't show error
          if (shareError.name === 'AbortError') {
            return;
          }
          // Fall through to clipboard copy
        }
      }

      // Fallback: Copy to clipboard with legacy support for Safari
      const copyToClipboard = (text) => {
        // Try modern clipboard API (requires HTTPS)
        if (navigator.clipboard && navigator.clipboard.writeText) {
          return navigator.clipboard.writeText(text)
            .then(() => true)
            .catch(() => false);
        }

        // Legacy fallback for Safari and non-HTTPS
        return new Promise((resolve) => {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          textArea.setAttribute('readonly', '');
          document.body.appendChild(textArea);

          // iOS Safari requires these steps
          if (navigator.userAgent.match(/ipad|iphone/i)) {
            const range = document.createRange();
            range.selectNodeContents(textArea);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
            textArea.setSelectionRange(0, 999999);
          } else {
            textArea.select();
          }

          let success = false;
          try {
            success = document.execCommand('copy');
          } catch (err) {
            success = false;
          }

          document.body.removeChild(textArea);
          resolve(success);
        });
      };

      const success = await copyToClipboard(url);

      if (success) {
        // Track successful share via clipboard
        trackProductShare(product, 'clipboard');
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } else {
        // Last resort: show the URL in an alert
        alert('Copy this link: ' + url);
      }

    } catch (error) {
      console.error('Share failed:', error);
      // Show URL as fallback
      alert('Share link: ' + url);
    }
  };

  // Get media array (use media if available, otherwise fall back to single image)
  const getProductMedia = () => {
    if (product.media && product.media.length > 0) {
      return product.media;
    }
    // Fallback to single image for backwards compatibility
    if (product.image || product.imageUrl) {
      return [{ url: product.image || product.imageUrl, type: 'image' }];
    }
    return [{ url: '/images/placeholder.jpg', type: 'image' }];
  };

  const media = getProductMedia();

  const nextMedia = () => {
    setCurrentMediaIndex((prev) => (prev + 1) % media.length);
  };

  const prevMedia = () => {
    setCurrentMediaIndex((prev) => (prev - 1 + media.length) % media.length);
  };

  // Swipe gesture handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && media.length > 1) {
      nextMedia();
    } else if (isRightSwipe && media.length > 1) {
      prevMedia();
    }
  };

  return (
    <div className="product-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="product-modal-title">
      <div className="product-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="product-modal-header-actions">
          <button
            className="product-modal-share-btn"
            onClick={handleShare}
            aria-label="Share product"
            title={shareCopied ? "Link copied!" : "Share product"}
          >
            {shareCopied ? <Check size={18} /> : <Send size={18} />}
          </button>
          <button
            className="product-modal-close"
            onClick={onClose}
            aria-label="Close product details"
            title="Close (ESC)"
          >
            <X size={20} />
          </button>
        </div>

        <div className="product-modal-grid">
          {/* Product Media Column with Carousel */}
          <div
            className="product-modal-image-container"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Current Media Display */}
            {media[currentMediaIndex].type === 'video' ? (
              <video
                src={media[currentMediaIndex].url}
                className="product-modal-image"
                controls
                autoPlay
                muted
                loop
              />
            ) : (
              <LazyImage
                src={media[currentMediaIndex].url}
                alt={`${product.name} ${currentMediaIndex + 1}`}
                className="product-modal-image"
                fallbackSrc="/images/placeholder.jpg"
                eager={true}
              />
            )}

            {/* Navigation Arrows (only show if more than 1 media item) */}
            {media.length > 1 && (
              <>
                <button
                  className="media-nav-btn media-nav-prev"
                  onClick={prevMedia}
                  aria-label="Previous media"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  className="media-nav-btn media-nav-next"
                  onClick={nextMedia}
                  aria-label="Next media"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}

            {/* Media Indicator Dots */}
            {media.length > 1 && (
              <div className="media-indicators">
                {media.map((_, index) => (
                  <button
                    key={index}
                    className={`media-indicator ${index === currentMediaIndex ? 'active' : ''}`}
                    onClick={() => setCurrentMediaIndex(index)}
                    aria-label={`Go to media ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Discount Badge */}
            {product.discount > 0 && (
              <div className="product-modal-discount-badge">
                {product.discount}% OFF
              </div>
            )}

            {/* Media Counter */}
            {media.length > 1 && (
              <div className="media-counter">
                {currentMediaIndex + 1} / {media.length}
              </div>
            )}
          </div>
          
          {/* Product Details Column */}
          <div className="product-modal-details">
            {/* Product Name */}
            <h2 id="product-modal-title" className="product-modal-title">{product.name}</h2>

            {/* Description */}
            <div className="product-modal-description">
              {product.description ? (
                <>
                  <div className={`description-text ${isDescriptionExpanded ? 'expanded' : 'collapsed'}`}>
                    <SafeHTMLRenderer content={product.description} />
                  </div>
                  {getPlainTextLength(product.description) > 150 && (
                    <button
                      className="read-more-btn"
                      onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                    >
                      {isDescriptionExpanded ? 'Read Less' : 'Read More'}
                    </button>
                  )}
                </>
              ) : (
                'No description available.'
              )}
            </div>

            {/* Note/Disclaimer */}
            {product.note && (
              <div className="product-modal-note">
                <p>{product.note}</p>
              </div>
            )}

            {/* Tags section */}
            {product.tags && product.tags.length > 0 && (
              <div className="product-modal-tags">
                {product.tags.map((tag, index) => (
                  <span key={index} className="product-modal-tag">{tag}</span>
                ))}
              </div>
            )}

            {/* Price */}
            <div className="product-modal-price-container">
              {discountedPrice ? (
                <>
                  <span className="product-modal-original-price">
                    R{parseFloat(product.price).toFixed(2)}
                  </span>
                  <span className="product-modal-discounted-price">
                    R{discountedPrice.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="product-modal-price">
                  R{parseFloat(product.price).toFixed(2)}
                </span>
              )}
            </div>
            
            {/* Quantity selector */}
            <div className="product-modal-quantity">
              <label>Quantity:</label>
              <div className="product-modal-quantity-controls">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="product-modal-quantity-btn"
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="product-modal-quantity-value">{quantity}</span>
                <button 
                  onClick={() => setQuantity(quantity + 1)}
                  className="product-modal-quantity-btn"
                >
                  +
                </button>
              </div>
            </div>
            
            {/* Add to cart button */}
            <button 
              className="product-modal-add-btn"
              onClick={handleAddToCart}
            >
              Add to Cart {quantity > 1 ? `(${quantity})` : ''}
            </button>
          </div>
        </div>
        
        {/* Related products section - only show if there are related products */}
        {(hasRelatedProducts || loading) && (
          <div className="product-modal-related">
            <h3 className="product-modal-related-title">You may also like</h3>
            
            {loading ? (
              <div className="product-modal-loading">Loading related products...</div>
            ) : (
              <div className="product-modal-related-grid">
                {relatedProducts.map(relatedProduct => (
                  <div 
                    key={relatedProduct.id} 
                    className="product-modal-related-item"
                    onClick={() => {
                      onClose();
                      // A slight delay before opening the new modal
                      setTimeout(() => {
                        // This assumes you have a function to open the modal with a specific product
                        if (typeof window !== 'undefined') {
                          const event = new CustomEvent('openProductModal', { 
                            detail: { productId: relatedProduct.id } 
                          });
                          window.dispatchEvent(event);
                        }
                      }, 100);
                    }}
                  >
                    <div className="product-modal-related-image-container">
                      <LazyImage 
                        src={relatedProduct.image || '/images/placeholder.jpg'} 
                        alt={relatedProduct.name}
                        className="product-modal-related-image"
                        fallbackSrc="/images/placeholder.jpg"
                      />
                    </div>
                    <div className="product-modal-related-details">
                      <h4 className="product-modal-related-name">{relatedProduct.name}</h4>
                      <span className="product-modal-related-price">
                        R{parseFloat(relatedProduct.price).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Toast Notification */}
        <Toast
          message={toastMessage}
          show={showToast}
          onClose={() => setShowToast(false)}
          type="cart"
          actionButton={{
            text: 'View Cart',
            onClick: () => navigate('/cart')
          }}
        />
      </div>
    </div>
  );
};

export default ProductDetailModal;